import { useEffect, useMemo, useState } from "react";
import {
  siListPackages, siCreatePackage, siUpdatePackage, siDeletePackage, siReorderPackages,
  siAdminGetImplementationRules, siSaveImplementationRules,
  siListAddons, siCreateAddon, siUpdateAddon, siDeleteAddon,
  siListGeo, activePricingRegions, costPerInspectionText,
} from "../lib/smartInspectApi";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Loader2, Package as PackageIcon, Puzzle, Building2 } from "lucide-react";

const BILLING_MODELS = ["Monthly Subscription", "Per Inspection", "Hybrid", "Custom"];
const ADDON_TYPES = ["Monthly", "Annual", "One-Time", "Per Inspection", "Scope Based", "Custom"];
const FEATURES = [
  ["smartinspect_operations", "SmartInspect Operations"],
  ["smartinspect_reports", "SmartInspect Reports"],
  ["sap_integration", "SAP Integration"],
  ["smartinspect_insights", "SmartInspect Insights"],
  ["priva_assistant", "PRIVA Assistant"],
  ["standard_support", "Standard Support"],
  ["future_product_updates", "Future Product Updates"],
];

const rSlug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-");
const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
// Coerce a { region: price } map to numbers, dropping empties. `allowText`
// keeps free-text values (Scope Based / Custom add-ons).
const cleanRegional = (m, allowText = false) => Object.fromEntries(
  Object.entries(m || {})
    .filter(([, v]) => v !== "" && v !== null && v !== undefined)
    .map(([k, v]) => {
      if (allowText) {
        const n = Number(v);
        return [k, (typeof v !== "boolean" && String(v).trim() !== "" && !isNaN(n)) ? n : v];
      }
      return [k, Number(v)];
    })
);

const EMPTY_PKG = {
  package_name: "", package_code: "", description: "", billing_model: "Monthly Subscription",
  monthly_inspection_limit: "", unlimited_inspections: false, monthly_subscription: "",
  custom_pricing: false, regional_pricing: {}, currency: "USD",
  active: true, show_in_roi_calculator: true, internal_only: false, display_order: 0,
  included_features: {}, included_addon_names: [],
};
const EMPTY_ADDON = { name: "", pricing_type: "Monthly", description: "", regional_pricing: {}, active: true, display_order: 0 };

function Badge({ on, onText = "Active", offText = "Inactive" }) {
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded-sm text-[9px] font-semibold ${on ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#F3F4F6] text-[#9CA3AF]"}`}>
      {on ? onText : offText}
    </span>
  );
}

// Inline-editable numeric price cell for a single region.
function PriceCell({ value, onChange, testid, allowText = false }) {
  return (
    <Input
      type={allowText ? "text" : "number"}
      className="h-8 text-xs w-full min-w-[92px]"
      data-testid={testid}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export default function SmartInspectCommercialPackagesPage() {
  const [packages, setPackages] = useState([]);
  const [addons, setAddons] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);

  // package metadata dialog + delete
  const [editing, setEditing] = useState(null);
  const [savingMeta, setSavingMeta] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [savingPkgPricing, setSavingPkgPricing] = useState(false);
  const [dirtyPkg, setDirtyPkg] = useState({}); // id -> true

  // add-on metadata dialog + delete
  const [editingAddon, setEditingAddon] = useState(null);
  const [savingAddonMeta, setSavingAddonMeta] = useState(false);
  const [deleteAddon, setDeleteAddonTarget] = useState(null);
  const [savingAddonPricing, setSavingAddonPricing] = useState(false);
  const [dirtyAddon, setDirtyAddon] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [pkgs, ads, geo] = await Promise.all([siListPackages(), siListAddons(), siListGeo()]);
      setPackages(pkgs);
      setAddons(ads);
      setRegions(activePricingRegions(geo));
      setDirtyPkg({}); setDirtyAddon({});
    } catch { toast.error("Failed to load commercial pricing"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const sortedPkgs = useMemo(() => [...packages].sort((a, b) => (a.display_order || 0) - (b.display_order || 0)), [packages]);
  const sortedAddons = useMemo(() => [...addons].sort((a, b) => (a.display_order || 0) - (b.display_order || 0)), [addons]);

  // ---------- Packages: inline pricing + toggles ----------
  const setPkgPrice = (id, region, v) => {
    setPackages((prev) => prev.map((p) => p.id === id ? { ...p, regional_pricing: { ...(p.regional_pricing || {}), [region]: v } } : p));
    setDirtyPkg((d) => ({ ...d, [id]: true }));
  };

  const savePkgPricing = async () => {
    const ids = Object.keys(dirtyPkg);
    if (ids.length === 0) { toast.info("No pricing changes to save"); return; }
    setSavingPkgPricing(true);
    try {
      const updated = await Promise.all(ids.map((id) => {
        const p = packages.find((x) => x.id === id);
        const rp = cleanRegional(p.regional_pricing);
        return siUpdatePackage(id, { ...p, regional_pricing: rp, monthly_subscription: rp.USA != null ? rp.USA : p.monthly_subscription });
      }));
      setPackages((prev) => prev.map((p) => updated.find((u) => u.id === p.id) || p));
      setDirtyPkg({});
      toast.success(`Saved pricing for ${ids.length} package${ids.length !== 1 ? "s" : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSavingPkgPricing(false); }
  };

  const togglePkg = async (p, field) => {
    try {
      const u = await siUpdatePackage(p.id, { ...p, regional_pricing: cleanRegional(p.regional_pricing), [field]: !p[field] });
      setPackages((prev) => prev.map((x) => (x.id === p.id ? u : x)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const movePkg = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= sortedPkgs.length) return;
    const a = sortedPkgs[idx], b = sortedPkgs[j];
    const ao = a.display_order || 0, bo = b.display_order || 0;
    setPackages((prev) => prev.map((x) => x.id === a.id ? { ...x, display_order: bo } : x.id === b.id ? { ...x, display_order: ao } : x));
    try { await siReorderPackages([{ id: a.id, display_order: bo }, { id: b.id, display_order: ao }]); }
    catch { toast.error("Reorder failed"); load(); }
  };

  // ---------- Package metadata dialog ----------
  const openNewPkg = () => setEditing({
    ...EMPTY_PKG,
    display_order: (sortedPkgs.length || 0) + 1,
    included_features: Object.fromEntries(FEATURES.map(([k]) => [k, true])),
    included_addon_names: sortedAddons.map((a) => a.name),
  });
  const openEditPkg = (p) => setEditing({
    ...EMPTY_PKG, ...p,
    monthly_inspection_limit: p.monthly_inspection_limit ?? "",
    included_addon_names: p.included_addon_names || [],
  });
  const setF = (patch) => setEditing((e) => ({ ...e, ...patch }));

  const savePkgMeta = async () => {
    if (!editing.package_name.trim() || !editing.package_code.trim()) { toast.error("Package name and code are required."); return; }
    setSavingMeta(true);
    try {
      const payload = {
        ...editing,
        regional_pricing: cleanRegional(editing.regional_pricing),
        monthly_inspection_limit: numOrNull(editing.monthly_inspection_limit),
      };
      if (editing.id) {
        const u = await siUpdatePackage(editing.id, payload);
        setPackages((prev) => prev.map((x) => (x.id === editing.id ? u : x)));
        toast.success("Package updated");
      } else {
        const c = await siCreatePackage(payload);
        setPackages((prev) => [...prev, c]);
        toast.success("Package created");
      }
      setEditing(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSavingMeta(false); }
  };

  const confirmDeletePkg = async () => {
    if (!deleteTarget) return;
    try { await siDeletePackage(deleteTarget.id); setPackages((prev) => prev.filter((x) => x.id !== deleteTarget.id)); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
    finally { setDeleteTarget(null); }
  };

  // ---------- Add-ons: inline pricing + toggles ----------
  const setAddonPrice = (id, region, v) => {
    setAddons((prev) => prev.map((a) => a.id === id ? { ...a, regional_pricing: { ...(a.regional_pricing || {}), [region]: v } } : a));
    setDirtyAddon((d) => ({ ...d, [id]: true }));
  };
  const saveAddonPricing = async () => {
    const ids = Object.keys(dirtyAddon);
    if (ids.length === 0) { toast.info("No pricing changes to save"); return; }
    setSavingAddonPricing(true);
    try {
      const updated = await Promise.all(ids.map((id) => {
        const a = addons.find((x) => x.id === id);
        const scoped = a.pricing_type === "Scope Based" || a.pricing_type === "Custom";
        return siUpdateAddon(id, { ...a, regional_pricing: cleanRegional(a.regional_pricing, scoped) });
      }));
      setAddons((prev) => prev.map((a) => updated.find((u) => u.id === a.id) || a));
      setDirtyAddon({});
      toast.success(`Saved pricing for ${ids.length} add-on${ids.length !== 1 ? "s" : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSavingAddonPricing(false); }
  };
  const toggleAddon = async (a) => {
    try {
      const u = await siUpdateAddon(a.id, { ...a, active: !a.active });
      setAddons((prev) => prev.map((x) => (x.id === a.id ? u : x)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const openNewAddon = () => setEditingAddon({ ...EMPTY_ADDON, display_order: (sortedAddons.length || 0) + 1 });
  const openEditAddon = (a) => setEditingAddon({ ...EMPTY_ADDON, ...a });
  const setAF = (patch) => setEditingAddon((e) => ({ ...e, ...patch }));
  const saveAddonMeta = async () => {
    if (!editingAddon.name.trim()) { toast.error("Add-on name is required."); return; }
    setSavingAddonMeta(true);
    try {
      const payload = { ...editingAddon, regional_pricing: cleanRegional(editingAddon.regional_pricing, true) };
      if (editingAddon.id) {
        const u = await siUpdateAddon(editingAddon.id, payload);
        setAddons((prev) => prev.map((x) => (x.id === editingAddon.id ? u : x)));
        toast.success("Add-on updated");
      } else {
        const c = await siCreateAddon(payload);
        setAddons((prev) => [...prev, c]);
        toast.success("Add-on created");
      }
      setEditingAddon(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSavingAddonMeta(false); }
  };
  const confirmDeleteAddon = async () => {
    if (!deleteAddon) return;
    try { await siDeleteAddon(deleteAddon.id); setAddons((prev) => prev.filter((x) => x.id !== deleteAddon.id)); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
    finally { setDeleteAddonTarget(null); }
  };

  const dirtyPkgCount = Object.keys(dirtyPkg).length;
  const dirtyAddonCount = Object.keys(dirtyAddon).length;

  return (
    <div data-testid="si-packages-page">
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "Chivo" }}>SmartInspect Commercial Packages</h1>
      </div>
      <p className="text-xs text-[#9CA3AF] mb-4 max-w-3xl leading-relaxed">
        A regional pricing matrix. Edit prices per Pricing Region inline — one column per region from
        Countries &amp; Regional Benchmarks. Package/add-on metadata is edited via the dialog.
      </p>

      {loading ? (
        <div className="mt-10 flex justify-center"><Loader2 className="animate-spin text-[#025979]" /></div>
      ) : regions.length === 0 ? (
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 text-xs text-amber-700">
          No active Pricing Regions configured. Add them in SmartInspect → Countries &amp; Regional Benchmarks first.
        </div>
      ) : (
        <>
          {/* ===================== PACKAGES ===================== */}
          <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm overflow-hidden" data-testid="si-package-table">
            <div className="px-4 py-3 bg-gray-50 border-b border-[#E5E7EB] flex items-center justify-between">
              <p className="text-xs font-medium text-[#111827] flex items-center gap-1.5"><PackageIcon className="w-3.5 h-3.5 text-[#025979]" /> Packages <span className="text-[10px] text-[#9CA3AF] font-normal">({sortedPkgs.length})</span></p>
              <div className="flex items-center gap-2">
                <Button data-testid="si-add-package-btn" variant="outline" onClick={openNewPkg} className="rounded-sm text-xs h-8">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add Package
                </Button>
                <Button data-testid="si-save-package-pricing" onClick={savePkgPricing} disabled={savingPkgPricing || dirtyPkgCount === 0} className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs h-8">
                  {savingPkgPricing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : `Save Pricing${dirtyPkgCount ? ` (${dirtyPkgCount})` : ""}`}
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-[#E5E7EB] bg-white">
                    <th className="text-center px-2 py-2 text-[#6B7280] font-medium w-14">Order</th>
                    <th className="text-left px-3 py-2 text-[#6B7280] font-medium min-w-[180px]">Package Name</th>
                    <th className="text-left px-3 py-2 text-[#6B7280] font-medium w-24">Monthly Limit</th>
                    {regions.map((r) => (
                      <th key={r.region} className="text-left px-3 py-2 text-[#6B7280] font-medium">{r.region}<div className="text-[9px] font-mono text-[#9CA3AF]">{r.currency_code}</div></th>
                    ))}
                    <th className="text-center px-2 py-2 text-[#6B7280] font-medium w-16">Active</th>
                    <th className="text-center px-2 py-2 text-[#6B7280] font-medium w-24">In Calculator</th>
                    <th className="text-right px-3 py-2 text-[#6B7280] font-medium w-20">Edit</th>
                  </tr>
                </thead>
                <tbody data-testid="si-package-tbody">
                  {sortedPkgs.map((p, idx) => (
                    <tr key={p.id} data-testid={`si-package-row-${p.package_code}`} className="border-b border-[#E5E7EB] hover:bg-gray-50">
                      <td className="px-2 py-2">
                        <div className="flex items-center justify-center gap-0.5">
                          <span className="text-[#9CA3AF] text-[10px] w-3 text-center">{idx + 1}</span>
                          <div className="flex flex-col">
                            <button data-testid={`si-package-moveup-${p.package_code}`} onClick={() => movePkg(idx, -1)} disabled={idx === 0} className="text-[#6B7280] hover:text-[#025979] disabled:text-[#E5E7EB]"><ArrowUp className="w-3 h-3" /></button>
                            <button data-testid={`si-package-movedown-${p.package_code}`} onClick={() => movePkg(idx, 1)} disabled={idx === sortedPkgs.length - 1} className="text-[#6B7280] hover:text-[#025979] disabled:text-[#E5E7EB]"><ArrowDown className="w-3 h-3" /></button>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-[#111827]">
                        <div className="font-medium">{p.package_name}</div>
                        <div className="text-[9px] text-[#9CA3AF] font-mono mt-0.5">{p.package_code} · {p.billing_model}</div>
                      </td>
                      <td className="px-3 py-2 text-[10px] text-[#6B7280]">{p.unlimited_inspections ? "Unlimited" : (p.monthly_inspection_limit != null ? Number(p.monthly_inspection_limit).toLocaleString() : "—")}</td>
                      {regions.map((r) => (
                        <td key={r.region} className="px-2 py-2 align-top">
                          <PriceCell
                            testid={`si-pkg-price-${p.package_code}-${rSlug(r.region)}`}
                            value={p.regional_pricing?.[r.region]}
                            onChange={(v) => setPkgPrice(p.id, r.region, v)}
                          />
                          <div className="text-[9px] text-slate-400 mt-0.5 leading-tight" data-testid={`si-pkg-cpi-${p.package_code}-${rSlug(r.region)}`}>
                            {costPerInspectionText({ price: p.regional_pricing?.[r.region], monthlyLimit: p.monthly_inspection_limit, unlimited: p.unlimited_inspections, custom: p.custom_pricing, currencyCode: r.currency_code })}
                          </div>
                        </td>
                      ))}
                      <td className="px-2 py-2 text-center">
                        <button data-testid={`si-package-toggle-active-${p.package_code}`} onClick={() => togglePkg(p, "active")} title="Activate / Deactivate"><Badge on={!!p.active} /></button>
                      </td>
                      <td className="px-2 py-2 text-center">
                        <Switch data-testid={`si-package-toggle-roi-${p.package_code}`} checked={!!p.show_in_roi_calculator} onCheckedChange={() => togglePkg(p, "show_in_roi_calculator")} />
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button data-testid={`si-package-edit-${p.package_code}`} onClick={() => openEditPkg(p)} className="text-[#025979] hover:text-[#03738C] mr-2"><Pencil className="w-3.5 h-3.5" /></button>
                        <button data-testid={`si-package-delete-${p.package_code}`} onClick={() => setDeleteTarget(p)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                      </td>
                    </tr>
                  ))}
                  {sortedPkgs.length === 0 && (
                    <tr><td colSpan={6 + regions.length} className="px-4 py-8 text-center text-[#9CA3AF]">No packages yet. <button onClick={openNewPkg} className="text-[#025979] font-medium">Add one</button></td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ===================== ADD-ONS ===================== */}
          <div className="mt-6 bg-white border border-[#E5E7EB] rounded-sm shadow-sm overflow-hidden" data-testid="si-addon-table">
            <div className="px-4 py-3 bg-gray-50 border-b border-[#E5E7EB] flex items-center justify-between">
              <p className="text-xs font-medium text-[#111827] flex items-center gap-1.5"><Puzzle className="w-3.5 h-3.5 text-[#025979]" /> Optional Add-ons <span className="text-[10px] text-[#9CA3AF] font-normal">({sortedAddons.length})</span></p>
              <div className="flex items-center gap-2">
                <Button data-testid="si-add-addon-btn" variant="outline" onClick={openNewAddon} className="rounded-sm text-xs h-8">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add Add-on
                </Button>
                <Button data-testid="si-save-addon-pricing" onClick={saveAddonPricing} disabled={savingAddonPricing || dirtyAddonCount === 0} className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs h-8">
                  {savingAddonPricing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : `Save Pricing${dirtyAddonCount ? ` (${dirtyAddonCount})` : ""}`}
                </Button>
              </div>
            </div>
            <p className="text-[11px] text-[#9CA3AF] px-4 pt-3 max-w-2xl leading-relaxed">
              Add-ons are shared across all packages. Control which packages offer them via each package's <b>Included Add-ons</b>.
            </p>
            <div className="overflow-x-auto p-1">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-[#E5E7EB]">
                    <th className="text-left px-3 py-2 text-[#6B7280] font-medium min-w-[180px]">Add-on Name</th>
                    <th className="text-left px-3 py-2 text-[#6B7280] font-medium w-32">Billing Model</th>
                    {regions.map((r) => (
                      <th key={r.region} className="text-left px-3 py-2 text-[#6B7280] font-medium">{r.region}<div className="text-[9px] font-mono text-[#9CA3AF]">{r.currency_code}</div></th>
                    ))}
                    <th className="text-center px-2 py-2 text-[#6B7280] font-medium w-16">Active</th>
                    <th className="text-right px-3 py-2 text-[#6B7280] font-medium w-20">Edit</th>
                  </tr>
                </thead>
                <tbody data-testid="si-addon-tbody">
                  {sortedAddons.map((a) => {
                    const scoped = a.pricing_type === "Scope Based" || a.pricing_type === "Custom";
                    return (
                      <tr key={a.id} data-testid={`si-addon-row-${rSlug(a.name)}`} className="border-b border-[#E5E7EB] hover:bg-gray-50">
                        <td className="px-3 py-2 text-[#111827]">
                          <div className="font-medium">{a.name}</div>
                          {a.description ? <div className="text-[9px] text-[#9CA3AF] mt-0.5">{a.description}</div> : null}
                        </td>
                        <td className="px-3 py-2 text-[10px] text-[#6B7280]">{a.pricing_type}</td>
                        {regions.map((r) => (
                          <td key={r.region} className="px-2 py-2">
                            <PriceCell
                              testid={`si-addon-price-${rSlug(a.name)}-${rSlug(r.region)}`}
                              value={a.regional_pricing?.[r.region]}
                              onChange={(v) => setAddonPrice(a.id, r.region, v)}
                              allowText={scoped}
                            />
                          </td>
                        ))}
                        <td className="px-2 py-2 text-center">
                          <button data-testid={`si-addon-toggle-active-${rSlug(a.name)}`} onClick={() => toggleAddon(a)}><Badge on={a.active !== false} /></button>
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <button data-testid={`si-addon-edit-${rSlug(a.name)}`} onClick={() => openEditAddon(a)} className="text-[#025979] hover:text-[#03738C] mr-2"><Pencil className="w-3.5 h-3.5" /></button>
                          <button data-testid={`si-addon-delete-${rSlug(a.name)}`} onClick={() => setDeleteAddonTarget(a)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                        </td>
                      </tr>
                    );
                  })}
                  {sortedAddons.length === 0 && (
                    <tr><td colSpan={4 + regions.length} className="px-4 py-8 text-center text-[#9CA3AF]">No add-ons yet. <button onClick={openNewAddon} className="text-[#025979] font-medium">Add one</button></td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* =============== IMPLEMENTATION INVESTMENT =============== */}
          <ImplementationInvestmentCard regions={regions} />
        </>
      )}

      {/* ===================== Package metadata dialog ===================== */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="si-package-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit Package" : "New Package"}</DialogTitle>
            <DialogDescription>Metadata only. Regional pricing is edited in the main table.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-5 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Package Name"><Input data-testid="si-pkg-name" value={editing.package_name} onChange={(e) => setF({ package_name: e.target.value })} /></Field>
                <Field label="Package Code (immutable once created)"><Input data-testid="si-pkg-code" value={editing.package_code} disabled={!!editing.id} onChange={(e) => setF({ package_code: e.target.value })} placeholder="e.g. starter" /></Field>
                <Field label="Description" span><Input data-testid="si-pkg-desc" value={editing.description || ""} onChange={(e) => setF({ description: e.target.value })} /></Field>
                <Field label="Billing Model">
                  <Select value={editing.billing_model} onValueChange={(v) => setF({ billing_model: v })}>
                    <SelectTrigger data-testid="si-pkg-billing"><SelectValue /></SelectTrigger>
                    <SelectContent>{BILLING_MODELS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label={`Monthly Inspection Limit${editing.unlimited_inspections ? " (Unlimited)" : ""}`}>
                  <Input type="number" data-testid="si-pkg-limit" value={editing.monthly_inspection_limit} disabled={editing.unlimited_inspections} onChange={(e) => setF({ monthly_inspection_limit: e.target.value })} />
                </Field>
                <Field label="Display Order"><Input type="number" value={editing.display_order ?? 0} onChange={(e) => setF({ display_order: Number(e.target.value) })} /></Field>
                <label className="flex items-center gap-2 text-xs text-slate-600"><Switch data-testid="si-pkg-unlimited" checked={!!editing.unlimited_inspections} onCheckedChange={(v) => setF({ unlimited_inspections: v })} /> Unlimited Inspections</label>
              </div>

              <Section title="Included Features">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {FEATURES.map(([k, lbl]) => (
                    <label key={k} className="flex items-center gap-2 text-xs text-slate-600">
                      <Switch data-testid={`si-pkg-feat-${k}`} checked={!!editing.included_features?.[k]} onCheckedChange={(v) => setF({ included_features: { ...editing.included_features, [k]: v } })} /> {lbl}
                    </label>
                  ))}
                </div>
              </Section>

              <Section title="Included Add-ons">
                {sortedAddons.length === 0 ? (
                  <p className="text-[11px] text-slate-400">No add-ons defined yet. Create them in the Optional Add-ons table.</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="si-pkg-included-addons">
                    {sortedAddons.map((a) => {
                      const on = (editing.included_addon_names || []).includes(a.name);
                      return (
                        <label key={a.id} className="flex items-center gap-2 text-xs text-slate-600">
                          <Switch
                            data-testid={`si-pkg-addon-toggle-${rSlug(a.name)}`}
                            checked={on}
                            onCheckedChange={(v) => setF({
                              included_addon_names: v
                                ? [...(editing.included_addon_names || []), a.name]
                                : (editing.included_addon_names || []).filter((n) => n !== a.name),
                            })}
                          /> {a.name}
                        </label>
                      );
                    })}
                  </div>
                )}
              </Section>

              <Section title="Visibility">
                <div className="flex flex-wrap gap-6">
                  <label className="flex items-center gap-2 text-xs text-slate-600"><Switch data-testid="si-pkg-active" checked={!!editing.active} onCheckedChange={(v) => setF({ active: v })} /> Active</label>
                  <label className="flex items-center gap-2 text-xs text-slate-600"><Switch data-testid="si-pkg-show-roi" checked={!!editing.show_in_roi_calculator} onCheckedChange={(v) => setF({ show_in_roi_calculator: v })} /> Show in ROI Calculator</label>
                  <label className="flex items-center gap-2 text-xs text-slate-600"><Switch data-testid="si-pkg-internal" checked={!!editing.internal_only} onCheckedChange={(v) => setF({ internal_only: v })} /> Internal Only</label>
                </div>
              </Section>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button data-testid="si-pkg-save" onClick={savePkgMeta} disabled={savingMeta}>{savingMeta ? <Loader2 size={16} className="animate-spin mr-1" /> : null} Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===================== Add-on metadata dialog ===================== */}
      <Dialog open={!!editingAddon} onOpenChange={(o) => !o && setEditingAddon(null)}>
        <DialogContent className="max-w-lg" data-testid="si-addon-dialog">
          <DialogHeader>
            <DialogTitle>{editingAddon?.id ? "Edit Add-on" : "New Add-on"}</DialogTitle>
            <DialogDescription>Metadata only. Regional pricing is edited in the main table.</DialogDescription>
          </DialogHeader>
          {editingAddon && (
            <div className="space-y-4 text-sm">
              <Field label="Add-on Name"><Input data-testid="si-addon-name" value={editingAddon.name} onChange={(e) => setAF({ name: e.target.value })} /></Field>
              <Field label="Description"><Input data-testid="si-addon-desc" value={editingAddon.description || ""} onChange={(e) => setAF({ description: e.target.value })} /></Field>
              <Field label="Billing Model">
                <Select value={editingAddon.pricing_type} onValueChange={(v) => setAF({ pricing_type: v })}>
                  <SelectTrigger data-testid="si-addon-billing"><SelectValue /></SelectTrigger>
                  <SelectContent>{ADDON_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <label className="flex items-center gap-2 text-xs text-slate-600"><Switch data-testid="si-addon-active" checked={editingAddon.active !== false} onCheckedChange={(v) => setAF({ active: v })} /> Active</label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingAddon(null)}>Cancel</Button>
            <Button data-testid="si-addon-save" onClick={saveAddonMeta} disabled={savingAddonMeta}>{savingAddonMeta ? <Loader2 size={16} className="animate-spin mr-1" /> : null} Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmations */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="si-package-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete Package</DialogTitle>
            <DialogDescription>Delete "{deleteTarget?.package_name}"? Historical ROI selections keep their pricing snapshot. This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} className="rounded-sm text-xs">Cancel</Button>
            <Button data-testid="si-package-confirm-delete" onClick={confirmDeletePkg} className="bg-red-500 hover:bg-red-600 text-white rounded-sm text-xs">Delete Package</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteAddon} onOpenChange={(o) => { if (!o) setDeleteAddonTarget(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="si-addon-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete Add-on</DialogTitle>
            <DialogDescription>Delete "{deleteAddon?.name}"? It will be removed from all packages. This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteAddonTarget(null)} className="rounded-sm text-xs">Cancel</Button>
            <Button data-testid="si-addon-confirm-delete" onClick={confirmDeleteAddon} className="bg-red-500 hover:bg-red-600 text-white rounded-sm text-xs">Delete Add-on</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-[#E5E7EB] rounded-sm p-4">
      <h3 className="text-xs font-semibold text-[#025979] uppercase tracking-wide mb-3">{title}</h3>
      {children}
    </div>
  );
}
function Field({ label, children, span }) {
  return (
    <div className={span ? "col-span-2" : ""}>
      <label className="block text-xs font-medium text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

// Implementation Investment as a regional pricing matrix (rows = location ranges,
// columns = Pricing Regions). The ROI engine picks the amount for the selected
// region (exact → USA → base).
function ImplementationInvestmentCard({ regions }) {
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { siAdminGetImplementationRules().then(setRules).catch(() => setRules([])); }, []);

  const upd = (i, patch) => setRules((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const updPrice = (i, region, v) => setRules((rs) => rs.map((r, idx) => idx === i ? { ...r, regional_investment: { ...(r.regional_investment || {}), [region]: v } } : r));
  const addRow = () => setRules((rs) => [...rs, { id: null, min_locations: "", max_locations: "", investment: "", regional_investment: {} }]);
  const rmRow = (i) => setRules((rs) => rs.filter((_, idx) => idx !== i));
  const move = (i, d) => setRules((rs) => {
    const j = i + d;
    if (j < 0 || j >= rs.length) return rs;
    const c = [...rs];
    [c[i], c[j]] = [c[j], c[i]];
    return c;
  });

  const save = async () => {
    const payload = rules.map((r) => ({
      id: r.id || undefined,
      min_locations: r.min_locations === "" ? null : Number(r.min_locations),
      max_locations: r.max_locations === "" || r.max_locations == null ? null : Number(r.max_locations),
      regional_investment: cleanRegional(r.regional_investment),
    }));
    setSaving(true);
    try {
      const saved = await siSaveImplementationRules(payload);
      setRules(saved);
      toast.success("Implementation Investment saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-6 bg-white border border-[#E5E7EB] rounded-sm shadow-sm overflow-hidden" data-testid="si-impl-rules-card">
      <div className="px-4 py-3 bg-gray-50 border-b border-[#E5E7EB] flex items-center justify-between">
        <p className="text-xs font-medium text-[#111827] flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-[#025979]" /> Implementation Investment</p>
        <Button data-testid="si-impl-rules-save" onClick={save} disabled={saving || !rules} className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs h-8">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
        </Button>
      </div>
      <p className="text-[11px] text-[#9CA3AF] px-4 pt-3 max-w-2xl leading-relaxed">
        The ROI engine reads the amount for the customer's Pricing Region based on their operating locations. Leave the maximum blank for the top tier (16+).
      </p>
      {!rules ? (
        <div className="p-6 flex justify-center"><Loader2 className="animate-spin text-[#025979]" /></div>
      ) : (
        <div className="p-4 overflow-x-auto">
          <table className="w-full text-xs" data-testid="si-impl-rules-table">
            <thead>
              <tr className="border-b border-[#E5E7EB] text-[#6B7280]">
                <th className="text-left px-2 py-2 font-medium w-24">Min Loc.</th>
                <th className="text-left px-2 py-2 font-medium w-24">Max Loc.</th>
                {regions.map((r) => (
                  <th key={r.region} className="text-left px-2 py-2 font-medium">{r.region}<div className="text-[9px] font-mono text-[#9CA3AF]">{r.currency_code}</div></th>
                ))}
                <th className="px-2 py-2 w-24" />
              </tr>
            </thead>
            <tbody>
              {rules.map((r, i) => (
                <tr key={r.id || i} className="border-b border-[#F3F4F6]" data-testid={`si-impl-rule-row-${i}`}>
                  <td className="px-2 py-2"><Input type="number" className="h-8 text-xs" data-testid={`si-impl-min-${i}`} value={r.min_locations} onChange={(e) => upd(i, { min_locations: e.target.value })} /></td>
                  <td className="px-2 py-2"><Input type="number" className="h-8 text-xs" data-testid={`si-impl-max-${i}`} placeholder="∞" value={r.max_locations ?? ""} onChange={(e) => upd(i, { max_locations: e.target.value })} /></td>
                  {regions.map((rg) => (
                    <td key={rg.region} className="px-2 py-2">
                      <Input type="number" className="h-8 text-xs min-w-[92px]" data-testid={`si-impl-price-${i}-${rSlug(rg.region)}`}
                        value={r.regional_investment?.[rg.region] ?? ""}
                        onChange={(e) => updPrice(i, rg.region, e.target.value)} />
                    </td>
                  ))}
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-1">
                      <button className="text-[#9CA3AF] hover:text-[#025979] disabled:opacity-30" disabled={i === 0} onClick={() => move(i, -1)}><ArrowUp className="w-3.5 h-3.5" /></button>
                      <button className="text-[#9CA3AF] hover:text-[#025979] disabled:opacity-30" disabled={i === rules.length - 1} onClick={() => move(i, 1)}><ArrowDown className="w-3.5 h-3.5" /></button>
                      <button className="text-red-400 hover:text-red-600" data-testid={`si-impl-remove-${i}`} onClick={() => rmRow(i)}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Button type="button" size="sm" variant="outline" className="mt-3" data-testid="si-impl-add-row" onClick={addRow}><Plus className="w-3 h-3 mr-1" /> Add Location Range</Button>
        </div>
      )}
    </div>
  );
}
